import { useEffect, useState } from "react";
import {
  XMarkIcon, CheckCircleIcon, XCircleIcon,
  UserIcon, CalendarIcon, BuildingOffice2Icon, ChatBubbleLeftIcon, StarIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { submitIdeaDecision } from "../../api/jury";
import { fetchIdeaFeedback } from "../../api/ideaFeedback";

export interface ReviewIdea {
  _id: string;
  title: string;
  problem: string;
  solution: string;
  status: string;
  submittedFinalAt?: string;
  teamId: { _id: string; teamName?: string } | string;
  hackathonId: { _id: string; title: string } | string;
  attachments?: Array<{ filename?: string; url?: string; type?: string; sizeBytes?: number }>;
}

type Props = {
  open: boolean;
  idea: ReviewIdea | null;
  panelId: string;
  existingDecision?: "selected" | "rejected" | null;
  onClose: () => void;
  onDecisionSubmit: (ideaId: string, decision: "selected" | "rejected", rating?: number) => void;
  sidebarWidth?: number;
};

const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function ReviewDecisionModal({
  open, idea, panelId, existingDecision, onClose, onDecisionSubmit, sidebarWidth = 230,
}: Props) {
  const { token } = useAuth();
  const [decision, setDecision] = useState<"selected" | "rejected" | null>(null);
  const [reason, setReason] = useState("");
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDecision(existingDecision ?? null);
    setReason("");
    setFeedbacks([]);
    if (!idea || !token) return;
    fetchIdeaFeedback(token, idea._id)
      .then((data) => setFeedbacks(data.items ?? []))
      .catch(() => {});
  }, [open, idea, token, existingDecision]);

  const handleSubmit = async () => {
    if (!decision || !idea || !token) return;
    const hackathonId = typeof idea.hackathonId === "string" ? idea.hackathonId : idea.hackathonId._id;
    const teamId = typeof idea.teamId === "string" ? idea.teamId : idea.teamId._id;
    setSubmitting(true);
    const payload: any = {
      ideaId: idea._id,
      hackathonId,
      panelId,
      teamId,
      decision,
      reason: reason.trim() || undefined,
    };
    try {
      await submitIdeaDecision(token, payload);
      toast.success(`Idea marked as ${decision}`);
      onDecisionSubmit(idea._id, decision);
      onClose();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to submit decision");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !idea) return null;

  const hackathonTitle = typeof idea.hackathonId === "string" ? "—" : idea.hackathonId.title;
  const teamName = typeof idea.teamId === "string" ? "—" : (idea.teamId.teamName ?? "—");

  // Detect if mobile based on window size
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} />
      <div
        className="fixed z-50 flex items-center justify-center"
        style={
          isMobile
            ? { top: 0, left: 0, right: 0, bottom: 0, padding: '8px' }
            : { top: 62, left: sidebarWidth, right: 0, bottom: 0, padding: '16px' }
        }
      >
        <div className="relative z-10 w-full h-[90vh] sm:h-auto sm:max-w-2xl sm:max-h-[85vh] flex flex-col rounded-lg sm:rounded-2xl bg-white shadow-2xl">

          {/* HEADER */}
          <div className="flex-shrink-0 flex items-center justify-between border-b border-gray-100 px-4 sm:px-6 py-3 sm:py-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Review & Decide</h2>
            <button onClick={onClose} className="rounded-lg p-1 hover:bg-gray-100 transition-colors flex-shrink-0">
              <XMarkIcon className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3 sm:py-5 space-y-4 sm:space-y-5">

            {/* Meta */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1.5"><UserIcon className="h-4 w-4 flex-shrink-0" /><span className="truncate">{teamName}</span></span>
              <span className="flex items-center gap-1.5"><BuildingOffice2Icon className="h-4 w-4 flex-shrink-0" /><span className="truncate">{hackathonTitle}</span></span>
              {idea.submittedFinalAt && (
                <span className="flex items-center gap-1.5">
                  <CalendarIcon className="h-4 w-4 flex-shrink-0" />Submitted {fmtDate(idea.submittedFinalAt)}
                </span>
              )}
            </div>

            {/* Title */}
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 break-words">{idea.title}</h3>

            {/* Problem */}
            <div className="rounded-sm bg-gray-50 p-3 sm:p-4">
              <p className="mb-1 text-xs sm:text-sm font-medium text-gray-900">Idea Statement</p>
              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{idea.problem}</p>
            </div>

            {/* Solution */}
            <div className="rounded-sm bg-gray-50 p-3 sm:p-4">
              <p className="mb-1 text-xs sm:text-sm font-medium text-gray-900">Proposed Solution</p>
              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{idea.solution}</p>
            </div>

            {/* Attachments */}
            {idea.attachments && idea.attachments.length > 0 && (
              <div className="rounded-sm bg-gray-50 p-3 sm:p-4">
                <p className="mb-2 sm:mb-3 text-xs sm:text-sm font-medium text-gray-900">Attachments</p>
                <div className="space-y-2 sm:space-y-3">
                  {idea.attachments.map((attachment, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => attachment.url && window.open(attachment.url, "_blank")}
                      className="w-full text-left rounded-sm border border-gray-200 bg-white px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700 transition hover:border-blue-300 hover:bg-blue-50"
                    >
                      <div className="flex items-center justify-between gap-2 sm:gap-4">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{attachment.filename || attachment.type || `Attachment ${index + 1}`}</p>
                          {attachment.sizeBytes != null && (
                            <p className="text-xs text-gray-500">{(attachment.sizeBytes / 1024).toFixed(1)} KB</p>
                          )}
                        </div>
                        <span className="text-xs font-medium text-blue-600 flex-shrink-0">Preview</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Mentor Feedback */}
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-900">
                <ChatBubbleLeftIcon className="h-4 w-4 text-blue-500 flex-shrink-0" /> Mentor Feedback
                {feedbacks.length > 0 && <span className="text-xs text-gray-400">({feedbacks.length})</span>}
              </p>
              {feedbacks.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No mentor feedback recorded.</p>
              ) : (
                <div className="space-y-2">
                  {feedbacks.map((fb, i) => (
                    <div key={fb._id ?? i} className="rounded border border-gray-100 px-3 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-800 truncate">{fb.mentorId?.name ?? "Mentor"}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{fmtDate(fb.createdAt)}</span>
                      </div>
                      {fb.rating != null && (
                        <span className="flex items-center gap-1 text-xs text-yellow-600 font-medium">
                          <StarIcon className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400 flex-shrink-0" /> {fb.rating}/5
                        </span>
                      )}
                      <p className="text-xs text-gray-600 mt-1">{fb.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Decision */}
            <div>
              <p className="mb-2 text-xs sm:text-sm font-medium text-gray-700">
                Decision <span className="text-red-500">*</span>
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setDecision("selected")}
                  disabled={true}
                  className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors cursor-not-allowed ${
                    decision === "selected"
                      ? "border-teal-500 bg-teal-50 text-teal-700"
                      : "border-gray-200 text-gray-600 bg-gray-50"
                  }`}
                >
                  <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" /> <span>Selected</span>
                </button>
                <button
                  onClick={() => setDecision("rejected")}
                  disabled={true}
                  className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors cursor-not-allowed ${
                    decision === "rejected"
                      ? "border-red-400 bg-red-50 text-red-600"
                      : "border-gray-200 text-gray-600 bg-gray-50"
                  }`}
                >
                  <XCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" /> <span>Rejected</span>
                </button>
              </div>
            </div>

            {/* Reason */}
            <div>
              <p className="mb-2 text-xs sm:text-sm font-medium text-gray-700">
                Reason <span className="text-xs text-gray-400">(Optional)</span>
              </p>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide feedback or reasoning for your decision..."
                disabled={true}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none bg-gray-50 cursor-not-allowed"
              />
            </div>
          </div>

          {/* FOOTER */}
          <div className="flex-shrink-0 flex items-center justify-end border-t border-gray-100 gap-3 px-4 sm:px-6 py-3 sm:py-4">
            <button onClick={onClose} className="w-full sm:w-auto rounded-lg px-4 sm:px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors">
              Close
            </button>
          </div>

        </div>
      </div>
    </>
  );
}
